import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../api/axios';

const DeviceSettingsContext = createContext();

export function useDeviceSettings() {
  return useContext(DeviceSettingsContext);
}

export function DeviceSettingsProvider({ children }) {
  // Settings are cached per device so switching the selected device never shows
  // another device's values (fleet-safe). Consumers read settingsByDevice[deviceId].
  const [settingsByDevice, setSettingsByDevice] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch device settings for a specific device and cache under its id.
  const fetchDeviceSettings = useCallback(async (deviceId) => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/device/settings', {
        params: { deviceId },
        timeout: 10000, // matches the backend device-settings wait window
      });
      setSettingsByDevice(prev => ({ ...prev, [deviceId]: response.data }));
    } catch (err) {
      // Don't block the app; just clear this device's cache and surface a soft error.
      console.warn('Device settings unavailable (microcontroller likely not connected).');
      setSettingsByDevice(prev => ({ ...prev, [deviceId]: {} }));
      setError('Device settings unavailable');
    } finally {
      setLoading(false);
    }
  }, []);

  // Optimistically cache + push settings to a specific device.
  const updateDeviceSettings = useCallback(async (newSettings, deviceId) => {
    if (!deviceId) {
      setError('No device selected');
      return;
    }
    setSettingsByDevice(prev => ({ ...prev, [deviceId]: newSettings }));
    try {
      await api.post('/device/settings', { ...newSettings, deviceId });
    } catch (err) {
      setError('Failed to update device settings');
    }
  }, []);

  return (
    <DeviceSettingsContext.Provider value={{
      settingsByDevice,
      fetchDeviceSettings,
      updateDeviceSettings,
      loading,
      error,
    }}>
      {children}
    </DeviceSettingsContext.Provider>
  );
}
